import { user$ } from "$root/functional-areas/user";
import {
	workspaceEntityManagementActions,
	workspaceFolderManagementActions,
} from "$root/functional-areas/workspace/entity-management";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import { importPortfolio } from "$root/pages/PortfoliosStudio/PortfolioStudioTitleActions";
import { createPortfolio } from "$root/pages/PortfoliosStudio/common/CreateEntities";
import { PortfolioStudioTab } from "$root/pages/PortfoliosStudio/portfolio-studio-tabs";
import { getPortfolioStudioUrl } from "$root/pages/PortfoliosStudio/usePortfolioStudioRedirect";
import { useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import { qualifier } from "$root/utils";
import { SmartWorkspaceStructureDTOTypeEnum } from "@eu.mdotm.api/smart-workspaces";
import type { IconName } from "@mdotm/shadcn/legacy/components";
import { useDeepEqualEffect } from "@mdotm/shadcn/legacy/react-extensions";
import { atomWithCompare, useAtomValue, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { unpromisify } from "@mdotm/shadcn/legacy/utils";
import type { DropdownDropdownItem } from "@mdotm/shadcn/mdotm/components";
import { findInTree, findPathInTree, mapTree } from "@mdotm/utils/collections";
import { noop } from "@mdotm/utils/runtime";
import { useQuery } from "@tanstack/react-query";
import Immutable from "immutable";
import { useLocation } from "react-router";
import { hasAccess } from "../AuthorizationGuard";
import { typedUrlForRoute, useTypedNavigation } from "../PlatformRouter/RoutesDef";
import { type AppDrawerTreeNode, HierarchicalTreeModule } from "./HierarchicalTreeModule";

const expandMap$ = atomWithCompare(Immutable.Map<string, boolean>());

export function PortfolioStudioModule() {
	const portfolioModuleWithCommonItems = usePortfolioModuleWithCommonItems();
	return <ModuleTree items={portfolioModuleWithCommonItems} />;
}
function ModuleTree({ items }: { items: AppDrawerTreeNode<string>[] }) {
	const expanded =
		items[0] &&
		findPathInTree<"children", AppDrawerTreeNode<string>>(items[0], (child) => child.active, "children")?.map(
			(x) => x.name,
		);
	useDeepEqualEffect(() => {
		if (expanded) {
			writeAtom(expandMap$, (map) => map.merge(expanded.map((name) => [name, true])));
		}
	}, [expanded]);
	return <HierarchicalTreeModule expandMap$={expandMap$} className="px-4" items={items} />;
}

export function usePortfolioModuleWithCommonItems() {
	const user = useAtomValue(user$);
	const commonModules = useCommonPortfolioModule();
	const workspaceModule = useWorkspaceModule();
	const portfolioModule = usePortfolioModule();
	const portfolioLabModule = usePortfolioLabModule();
	return [
		hasAccess(user, { requiredService: "SMART_WORKSPACE" }) ? workspaceModule : portfolioModule,
		...commonModules,
		...(hasAccess(user, { requiredService: "PORTFOLIO_LAB" }) ? [portfolioLabModule] : []),
	];
}

function usePortfolioLabModule(): AppDrawerTreeNode<"PortfolioLab"> {
	const location = useLocation();
	const labUrl = typedUrlForRoute("PortfolioLab", {});

	return {
		qualifier: qualifier.appDrawer.portfolioStudioModules("PortfolioLab"),
		name: "PortfolioLab",
		title: "Portfolio Lab",
		href: labUrl,
		active: location.pathname.startsWith(labUrl),
		icon: "Ptf-lab" satisfies IconName,
	};
}

function usePortfolioModule(): AppDrawerTreeNode<"Portfolios"> {
	const location = useLocation();

	return {
		qualifier: qualifier.appDrawer.portfolioStudioModules("Portfolios"),
		name: "Portfolios",
		title: "Portfolios",
		href: getPortfolioStudioUrl(),
		active:
			(location.pathname + location.search).startsWith(typedUrlForRoute("PortfoliosStudio", {})) &&
			(!location.search.includes("tab=") || location.search.includes(`tab=${PortfolioStudioTab.Portfolios}`)),
		icon: "Portfolio" satisfies IconName,
		actions: [
			[
				"",
				[
					{
						"data-qualifier": qualifier.appDrawer.moduleActions("Portfolios", "Import"),
						label: "Import portfolio",
						icon: "Upload" satisfies IconName,
						onClick: unpromisify(importPortfolio),
					},
					{
						"data-qualifier": qualifier.appDrawer.moduleActions("Portfolios", "NewPortfolio"),
						label: "New portfolio",
						icon: "Portfolio" satisfies IconName,
						onClick: createPortfolio,
					},
				],
			],
		],
	};
}

function useWorkspaceModule(): AppDrawerTreeNode<"Portfolios"> {
	const { push } = useTypedNavigation();
	const location = useLocation();
	const user = useAtomValue(user$);

	const workspaceTreeQueryRaw = useQuery({
		...workspaceRepository.tree.getQueryConfig(),
		keepPreviousData: true,
		enabled: hasAccess(user, { requiredService: "SMART_WORKSPACE" }),
	});

	const defaultWorkspaceQueryRaw = useMappedQuery(
		(tree) =>
			findInTree(
				tree,
				(node) => node.name === "Portfolios" && node.type === SmartWorkspaceStructureDTOTypeEnum.Smartworkspace,
				"children",
			),
		[],
		workspaceTreeQueryRaw,
	);

	const folderActions = workspaceFolderManagementActions({
		name: "Workspace example",
		taxonomy: ["ROOT", "Workspace example"],
	});

	if (!workspaceTreeQueryRaw.data || !defaultWorkspaceQueryRaw.data) {
		return {
			qualifier: qualifier.appDrawer.portfolioStudioModules("Portfolios"),
			name: "Portfolios",
			title: "Portfolios",
			href: getPortfolioStudioUrl(),
			active: (location.pathname + location.search).startsWith(
				typedUrlForRoute("PortfolioStudio/DefaultWorkspace", {}),
			),
			icon: "Portfolio" satisfies IconName,
			count: undefined,
			actions: [
				[
					"",
					[
						{
							"data-qualifier": qualifier.appDrawer.moduleActions("Portfolios", "Import"),
							label: "Import portfolio",
							icon: "Upload" satisfies IconName,
							onClick: unpromisify(importPortfolio),
						},
						{
							"data-qualifier": qualifier.appDrawer.moduleActions("Portfolios", "NewPortfolio"),
							label: "New portfolio",
							icon: "Portfolio" satisfies IconName,
							onClick: createPortfolio,
						},
					],
				],
			],
		};
	}

	const workspaceActions = workspaceEntityManagementActions(
		{
			name: defaultWorkspaceQueryRaw.data.name,
			taxonomy: ["ROOT"],
			uuid: defaultWorkspaceQueryRaw.data.smartWorkspaceUuid!,
		},
		{
			onCreate(uuid) {
				push("PortfolioStudio/Workspace", { id: uuid });
			},
		},
	);

	return {
		qualifier: qualifier.appDrawer.portfolioStudioModules("Portfolios"),
		name: "Portfolios",
		title: "Portfolios",
		href: getPortfolioStudioUrl(),
		active: (location.pathname + location.search).startsWith(typedUrlForRoute("PortfolioStudio/DefaultWorkspace", {})),
		icon: "Portfolio" satisfies IconName,
		count: defaultWorkspaceQueryRaw.data.itemCount,
		actions: [
			[
				"",
				[
					{
						"data-qualifier": qualifier.appDrawer.moduleActions("Portfolios", "Import"),
						label: "Import portfolio",
						icon: "Upload" satisfies IconName,
						onClick: unpromisify(importPortfolio),
					},
					{
						"data-qualifier": qualifier.appDrawer.moduleActions("Portfolios", "NewPortfolio"),
						label: "New portfolio",
						icon: "Portfolio" satisfies IconName,
						onClick: createPortfolio,
					},
				],
			],
			[
				"",
				[
					{
						"data-qualifier": qualifier.appDrawer.moduleActions("Portfolios", "NewFolder"),
						label: "New folder",
						icon: "folder" satisfies IconName,
						onClick() {
							folderActions.createAsync().catch(noop);
						},
					},
					{
						"data-qualifier": qualifier.appDrawer.moduleActions("Portfolios", "NewWorkspace"),
						label: "New workspace",
						icon: "workspace" satisfies IconName,
						onClick() {
							workspaceActions.createAsync?.().catch(noop);
						},
					},
				],
			] as [string, DropdownDropdownItem[]],
		],
		children: mapTree(
			workspaceTreeQueryRaw.data,
			(child): AppDrawerTreeNode | null => {
				// Filter out the default workspace
				if (child.smartWorkspaceUuid === defaultWorkspaceQueryRaw.data?.smartWorkspaceUuid) {
					return null;
				}

				if (child.smartWorkspaceUuid == null || child.type === "FOLDER") {
					const workspaceActions = workspaceEntityManagementActions({
						name: defaultWorkspaceQueryRaw.data!.name,
						uuid: defaultWorkspaceQueryRaw.data!.smartWorkspaceUuid!,
						// TODO: should technically contain the complete taxonomy,
						// but for now it will do as we only allow users to create folders as children of ROOT
						taxonomy: ["ROOT", child.name, "new"],
					});

					const folderManagementActions = workspaceFolderManagementActions(
						{
							name: child.name,
							// TODO: should technically contain the complete taxonomy,
							// but for now it will do as we only allow users to create folders as children of ROOT
							taxonomy: ["ROOT", child.name],
						},
						{
							onDelete() {
								push("PortfolioStudio/DefaultWorkspace", {});
							},
						},
					);

					return {
						qualifier: qualifier.appDrawer.workspace(child),
						title: child.name,
						name: child.name,
						icon: "folder",
						active: false,
						href: undefined,
						actions: [
							[
								"",
								[
									{
										disabled: !workspaceActions.createAsync,
										label: "New workspace",
										icon: "workspace" satisfies IconName,
										onClick() {
											workspaceActions.createAsync?.().catch(noop);
										},
									},
								],
							],
							[
								"",
								[
									{
										disabled: !folderManagementActions.renameAsync,
										label: "Rename",
										icon: "Edit" satisfies IconName,
										onClick() {
											folderManagementActions.renameAsync?.().catch(noop);
										},
									},
									{
										disabled: !folderManagementActions.deleteAsync,
										label: "Delete",
										icon: "Delete" satisfies IconName,
										onClick() {
											folderManagementActions.deleteAsync?.().catch(noop);
										},
									},
								],
							],
						],
					};
				}

				const workspaceURL = typedUrlForRoute("PortfolioStudio/Workspace", { id: child.smartWorkspaceUuid });
				const taxonomy =
					findPathInTree(
						workspaceTreeQueryRaw.data,
						(node) => node.smartWorkspaceUuid === child.smartWorkspaceUuid,
						"children",
					)?.map((x) => x.name) ?? [];

				const entityManagementActions = workspaceEntityManagementActions(
					{
						name: child.name,
						taxonomy: taxonomy.slice(0, -1), // exclude self
						uuid: child.smartWorkspaceUuid,
					},
					{
						onDelete() {
							if ((location.pathname + location.search).startsWith(workspaceURL)) {
								push("PortfolioStudio/DefaultWorkspace", {});
							}
						},
						onDuplicate(uuid) {
							push("PortfolioStudio/Workspace", { id: uuid });
						},
					},
				);

				return {
					qualifier: qualifier.appDrawer.workspace(child),
					title: child.name,
					name: child.name,
					count: child.itemCount,
					icon: "workspace" satisfies IconName,
					active: (location.pathname + location.search).startsWith(workspaceURL),
					href: workspaceURL,
					actions: !entityManagementActions
						? undefined
						: [
								{
									disabled: !entityManagementActions.renameAsync,
									label: "Rename",
									icon: "Edit" satisfies IconName,
									onClick() {
										entityManagementActions.renameAsync?.().catch(noop);
									},
								},
								{
									disabled: !entityManagementActions.duplicateAsync,
									label: "Duplicate",
									icon: "Content-Copy" satisfies IconName,
									onClick() {
										entityManagementActions.duplicateAsync?.().catch(noop);
									},
								},
								{
									disabled: !entityManagementActions.moveToFolder,
									label: "Move to folder",
									icon: "Drag-and-drop" satisfies IconName,
									onClick() {
										entityManagementActions.moveToFolder?.().catch(noop);
									},
								},
								{
									disabled: !entityManagementActions.deleteAsync,
									label: "Delete",
									icon: "Delete" satisfies IconName,
									onClick() {
										entityManagementActions.deleteAsync?.().catch(noop);
									},
								},
							],
				};
			},
			"children",
		)?.children,
	};
}

function useCommonPortfolioModule(): AppDrawerTreeNode<"References" | "Universes" | "MarketViews">[] {
	const location = useLocation();

	return [
		{
			qualifier: qualifier.appDrawer.portfolioStudioModules("References"),
			name: "References",
			title: "References",
			href: typedUrlForRoute("PortfoliosStudio", { tab: PortfolioStudioTab.References }),
			active: (location.pathname + location.search).startsWith(
				typedUrlForRoute("PortfoliosStudio", { tab: PortfolioStudioTab.References }),
			),
			icon: "References" satisfies IconName, // missing target reference icon from library
		},
		{
			qualifier: qualifier.appDrawer.portfolioStudioModules("Universes"),
			name: "Universes",
			title: "Universes",
			href: typedUrlForRoute("PortfoliosStudio", { tab: PortfolioStudioTab.Universes }),
			active: (location.pathname + location.search).startsWith(
				typedUrlForRoute("PortfoliosStudio", { tab: PortfolioStudioTab.Universes }),
			),
			icon: "universe" satisfies IconName,
		},
		{
			qualifier: qualifier.appDrawer.portfolioStudioModules("MarketViews"),
			name: "MarketViews",
			title: "Market views",
			href: typedUrlForRoute("PortfoliosStudio", { tab: PortfolioStudioTab.MarketViews }),
			active: (location.pathname + location.search).startsWith(
				typedUrlForRoute("PortfoliosStudio", { tab: PortfolioStudioTab.MarketViews }),
			),
			icon: "market-view" satisfies IconName,
		},
	];
}
